import { EquipmentRecordStatus, ProductLineUnmatchedReport, ProgramType } from 'types';

import { api } from './api';
import type { EquipmentHistoryEntry, EquipmentMeta } from './equipmentManufacturers';

const productLineTag = 'ProductLines' as const;
const listTag = { type: productLineTag, id: 'LIST' } as const;

/**
 * Frontend-facing shape of `api/src/schemas/productLines.ts`, declared locally like the manufacturers
 * service so the portal does not couple to the server document types (`ObjectId`, `Date`).
 */
export interface ProductLine {
  id: string;
  name: string;
  programType: ProgramType.energyBackup;
  classificationPath: string;
  values: string[];
  status: EquipmentRecordStatus;
  history: EquipmentHistoryEntry[];
  createdByName?: string;
  modifiedByName?: string;
  meta?: EquipmentMeta;
}

export interface ProductLineListResponse {
  data: ProductLine[];
  total: number;
}

export interface ListProductLinesParams {
  status?: EquipmentRecordStatus;
  programType?: ProgramType.energyBackup;
}

export interface CreateProductLineRequest {
  name: string;
  values: string[];
}

export interface UpdateProductLineValuesRequest {
  id: string;
  values: string[];
}

const listQueryString = (params: ListProductLinesParams): string => {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set('status', params.status);
  if (params.programType) searchParams.set('programType', params.programType);
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
};

export const productLinesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProductLines: build.query<ProductLineListResponse, ListProductLinesParams>({
      query: (params) => ({ url: `/v2/product-lines${listQueryString(params)}` }),
      providesTags: (result) =>
        result ? [...result.data.map(({ id }) => ({ type: productLineTag, id })), listTag] : [listTag],
    }),

    getProductLine: build.query<ProductLine, string>({
      query: (id) => ({ url: `/v2/product-lines/${id}` }),
      providesTags: (_result, _error, id) => [{ type: productLineTag, id }],
    }),

    // Shares the LIST tag so any line write, which changes what is matched, refreshes the report.
    getProductLineUnmatched: build.query<ProductLineUnmatchedReport, ProgramType.energyBackup | void>({
      query: (programType) => ({
        url: `/v2/product-lines/unmatched${programType ? `?programType=${programType}` : ''}`,
      }),
      providesTags: [listTag],
    }),

    createProductLine: build.mutation<ProductLine, CreateProductLineRequest>({
      query: (body) => ({ url: '/v2/product-lines', method: 'POST', body }),
      invalidatesTags: [listTag],
    }),

    updateProductLineValues: build.mutation<ProductLine, UpdateProductLineValuesRequest>({
      query: ({ id, values }) => ({ url: `/v2/product-lines/${id}/values`, method: 'PUT', body: { values } }),
      invalidatesTags: (_result, _error, { id }) => [{ type: productLineTag, id }, listTag],
    }),

    deactivateProductLine: build.mutation<ProductLine, string>({
      query: (id) => ({ url: `/v2/product-lines/${id}/deactivate`, method: 'POST', body: {} }),
      invalidatesTags: (_result, _error, id) => [{ type: productLineTag, id }, listTag],
    }),
  }),
});

export const {
  useGetProductLinesQuery,
  useGetProductLineQuery,
  useGetProductLineUnmatchedQuery,
  useCreateProductLineMutation,
  useUpdateProductLineValuesMutation,
  useDeactivateProductLineMutation,
} = productLinesApi;
